import { useCallback, useEffect, useState } from 'react';
import { getOrderMetrics } from './dashboardApi';
import type { OrderMetricsPeriod } from './dashboardApi';
import { getTodayDateStringKST } from '@/shared/lib/dashboard/dateRange';

export type DashboardMetricsDateParam = string | undefined;

export interface DashboardPeriodMetrics {
  date: string;
  periodType: 'NIGHT' | 'BUSINESS';
  periodLabel: string;
  received: number;
  processing: number;
  lost: number;
  churnRate: number;
  requote: number;
  unqualified: number;
  frt: { averageSeconds: number; medianSeconds: number } | null;
}

export interface DashboardMetrics {
  date: string;
  dateLabel: string;
  periods: DashboardPeriodMetrics[];
}

function toPeriodMetrics(p: OrderMetricsPeriod): DashboardPeriodMetrics {
  const churnRate =
    p.receivedCount > 0 ? Math.round((p.closedLostCount / p.receivedCount) * 1000) / 10 : 0;
  return {
    date: p.date,
    periodType: p.periodType,
    periodLabel: p.periodLabel,
    received: p.receivedCount,
    processing: p.pendingCount,
    lost: p.closedLostCount,
    churnRate,
    requote: p.requoteCount,
    unqualified: p.unqualifiedCount,
    frt: p.frt,
  };
}

function formatDateLabel(dateStr: string): string {
  const [year, month, date] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, date);
  return d.toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Seoul',
  });
}

async function fetchMetrics(targetDate: string): Promise<DashboardMetrics> {
  const { periods } = await getOrderMetrics({ date: targetDate });

  return {
    date: targetDate,
    dateLabel: formatDateLabel(targetDate),
    periods: periods.map(toPeriodMetrics),
  };
}

export function useDashboardMetrics(dateParam?: DashboardMetricsDateParam) {
  const date = dateParam ?? getTodayDateStringKST();
  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const mutate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchMetrics(date);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, [date]);

  useEffect(() => {
    mutate();
  }, [mutate]);

  return {
    data,
    error,
    isLoading,
    mutate,
  };
}
